import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPlus, FiMinus, FiChevronLeft, FiChevronRight } = FiIcons;

const MenuCard = ({ piatto, onAdd, onRemove, quantity = 0, showControls = false }) => {
  const [currentImg, setCurrentImg] = useState(0);

  // Estrazione ultra-resiliente delle immagini (supporta sia array immagini[] che stringa singola immagine)
  const rawImmagini = piatto.immagini || piatto.immagine || [];
  const immagini = Array.isArray(rawImmagini)
    ? rawImmagini.filter(img => typeof img === 'string' && img.trim() !== '')
    : (typeof rawImmagini === 'string' && rawImmagini.trim() !== '' ? [rawImmagini.trim()] : []);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(price);
  };

  const nextImg = (e) => {
    e.stopPropagation();
    setCurrentImg((prev) => (prev + 1) % immagini.length);
  };

  const prevImg = (e) => {
    e.stopPropagation();
    setCurrentImg((prev) => (prev - 1 + immagini.length) % immagini.length);
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }} 
      animate={{ opacity: 1, y: 0 }} 
      className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow flex flex-col h-full"
    >
      {/* Image Slider Section */}
      <div className="relative aspect-video rounded-lg overflow-hidden mb-3 bg-gray-100 group">
        {immagini.length > 0 ? (
          <>
            <AnimatePresence mode="wait">
              <motion.img
                key={currentImg}
                src={immagini[currentImg]}
                alt={`${piatto.nome} - ${currentImg + 1}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="w-full h-full object-cover"
              />
            </AnimatePresence>
            
            {immagini.length > 1 && (
              <>
                <button 
                  onClick={prevImg}
                  className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/30 hover:bg-black/50 text-white p-1.5 rounded-full backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <SafeIcon icon={FiChevronLeft} className="w-4 h-4" />
                </button>
                <button 
                  onClick={nextImg}
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/30 hover:bg-black/50 text-white p-1.5 rounded-full backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <SafeIcon icon={FiChevronRight} className="w-4 h-4" />
                </button>
                
                {/* Dots indicator */}
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex space-x-1.5">
                  {immagini.map((_, idx) => (
                    <div 
                      key={idx} 
                      className={`w-1.5 h-1.5 rounded-full transition-all ${idx === currentImg ? 'bg-white scale-125' : 'bg-white/50'}`}
                    />
                  ))}
                </div>
              </>
            )}
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400 italic text-sm">
            Nessuna immagine
          </div>
        )}
      </div>

      <div className="space-y-2 flex-1 flex flex-col">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-semibold text-gray-900 text-lg leading-tight">{piatto.nome}</h3>
          <span className="text-lg font-bold text-[#5C715E] whitespace-nowrap">
            {formatPrice(piatto.prezzo)}
          </span>
        </div>
        
        <p className="text-xs text-gray-400 font-bold uppercase tracking-wider">
          {piatto.categoria}
        </p>

        {piatto.descrizione && (
          <p className="text-gray-600 text-sm leading-relaxed line-clamp-2">
            {piatto.descrizione}
          </p>
        )}

        <div className="mt-auto pt-3">
          {!piatto.disponibile ? (
            <div className="bg-red-50 text-red-700 px-3 py-2 rounded-lg text-sm font-medium text-center">
              Non disponibile
            </div>
          ) : showControls && (
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <button 
                  onClick={() => onRemove(piatto)} 
                  disabled={quantity === 0}
                  className="w-10 h-10 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center disabled:opacity-30 hover:bg-gray-200 transition-colors"
                >
                  <SafeIcon icon={FiMinus} className="w-5 h-5" />
                </button>
                <span className="font-bold text-xl min-w-[1.5rem] text-center">
                  {quantity}
                </span>
                <button 
                  onClick={() => onAdd(piatto)} 
                  className="w-10 h-10 rounded-full bg-[#5C715E] text-white flex items-center justify-center hover:bg-[#4B5C4D] transition-colors shadow-sm"
                >
                  <SafeIcon icon={FiPlus} className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default MenuCard;